import { createAsyncThunk } from "@reduxjs/toolkit"
import { fetchApiGateway, extractApiGatewayError } from "@nova/fetch-api-gateway"
import { datadogRum } from "@datadog/browser-rum"
import { getDomainPrefix, SupportedRegions } from "@nova/constants"
import { config } from "@nova/configuration/browser"
import { RootState } from "integrations/redux"
import { selectNeedsCredentialsRefresh } from "./selectors"
import { userLogoutAction } from "user/authorization/actions"
import { isValidString } from "tools/strings/isNonEmptyString"
import { GenericRejectionResponseWithReason } from "integrations/redux/types"
import { registerAccount } from "user/registration/thunks"
import { UtmParams } from "user/registration/types"
import { getBrowserFingerprint } from "tools/browser/fingerprint"
import { fetchMe } from "user/profile/thunks"
import { makeBaseEndpoint } from "themes/default-tailwind/authentication/federated/module"
import { MicrosoftAuthRequest, NovaAuthStrategy } from "@nova/authentication/src/types"
import {
    AuthenticationChallengeRequest,
    PasswordPolicyResponse,
    PasswordResetResponse,
} from "./types"
import { forceClearAllAppCookies } from "./authUtils/forceClearAuthCookies"

enum AuthAction {
    Login = "user_login",
    Refresh = "user_refresh",
    Logout = "user_logout",
}

type AuthenticationThunkResponse = ThunkResponseIndicator &
    (AuthenticationThunkSuccessResponse | AuthenticationThunkRejectionResponse)

export const answerLoginChallenge = createAsyncThunk<
    AuthenticationThunkResponse,
    {
        email: string
        firstName: string
        lastName: string
        campaignTrackingMetrics: UtmParams
        challengeResponse: string
    }
>(
    "authentication/answerLoginChallenge",
    async (
        { email, firstName, lastName, challengeResponse, campaignTrackingMetrics },
        thunkApi
    ) => {
        //log the registration in dynamo before completing the account in cognito
        const registerResponse = await thunkApi.dispatch(
            registerAccount({ email, campaignTrackingMetrics })
        )
        if (!registerResponse.payload) {
            return thunkApi.rejectWithValue(
                "Couldn't finalize registration when adding account to registrations table"
            )
        }

        const data = await fetchApiGateway("post /auth/challenge", {
            headers: {
                "nova-auth-strategy": NovaAuthStrategy.COOKIES,
            },
            body: {
                email,
                givenName: firstName,
                familyName: lastName,
                response: challengeResponse,
                campaignTrackingMetrics,
            },
            region: SupportedRegions.Canada,
        })

        const processedResult = processAuthenticationResponseResults({ data, status: 200 })

        // for multiple challenges, we need to revive this:
        // const authenticationChallenge = get(result, "challenge")
        // if (authenticationChallenge) {
        //     return thunkApi.rejectWithValue({
        //         authenticationChallenge,
        //         message: "Must complete authentication challenge",
        //     })
        // }

        thunkApi.dispatch(fetchMe())

        return processedResult
    }
)

export const verifyAppleLogin = createAsyncThunk<
    AuthenticationThunkResponse,
    { appleCallbackParam: string }
>("authentication/verifyAppleLogin", async ({ appleCallbackParam }, thunkApi) => {
    const base64 = appleCallbackParam.replace(/-/g, "+").replace(/_/g, "/")
    const jsonString = Buffer.from(base64, "base64").toString("utf-8")
    const appleAuthCredential = JSON.parse(jsonString)

    try {
        const data = await fetchApiGateway("post /auth/apple/verify", {
            body: appleAuthCredential,
            region: SupportedRegions.Canada,
        })

        if (typeof window !== "undefined") sessionStorage.removeItem("isFirstLogin")

        const processedResult = processAuthenticationResponseResults({ data, status: 200 })

        if (!processedResult.success) {
            return thunkApi.rejectWithValue(processedResult)
        }

        thunkApi.dispatch(fetchMe())

        return processedResult
    } catch (error) {
        const apiError = extractApiGatewayError(error)
        if (apiError) {
            console.error(apiError)
            return thunkApi.rejectWithValue(apiError.details)
        }

        return thunkApi.rejectWithValue(error)
    }
})

export const verifyMicrosoftLogin = createAsyncThunk<
    AuthenticationThunkResponse,
    { microsoftCallbackCode: string }
>("authentication/verifyMicrosoftLogin", async ({ microsoftCallbackCode }, thunkApi) => {
    const request: MicrosoftAuthRequest = {
        code: microsoftCallbackCode,
        clientId: config.get("microsoftClientId"),
        redirectUri: makeBaseEndpoint() + "/login",
    }

    try {
        const data = await fetchApiGateway("post /auth/microsoft/verify", {
            body: request,
            region: SupportedRegions.Canada,
        })

        if (typeof window !== "undefined") sessionStorage.removeItem("isFirstLogin")

        const processedResult = processAuthenticationResponseResults({ data, status: 200 })

        if (!processedResult.success) {
            return thunkApi.rejectWithValue(processedResult)
        }

        thunkApi.dispatch(fetchMe())

        return processedResult
    } catch (error) {
        const apiError = extractApiGatewayError(error)
        if (apiError) {
            console.error(apiError)
            return thunkApi.rejectWithValue(apiError.details)
        }

        return thunkApi.rejectWithValue(error)
    }
})

type ThunkResponseIndicator = {
    success: boolean
    message?: string
}

export type AuthenticationThunkSuccessResponse = {
    id: string
    email: string
    firstName: string
    lastName: string
}

type AuthenticationThunkRejectionResponse = {
    authenticationChallenge?: AuthenticationChallengeRequest
}

function processAuthenticationResponseResults({
    data,
    status,
}: {
    data:
        | {
              challenge: {
                  requireEmailConfirmationCode: boolean
                  requireNewPassword: boolean
                  requireUserAttributes: boolean
                  requireDeviceConfirmation: boolean
                  deviceKey: string
                  names: string[]
                  message: string
              }
          }
        | {
              user: {
                  id: string
                  email: string
                  firstName: string
                  lastName: string
              }
          }
    status?: number
}): AuthenticationThunkResponse {
    const requestError = !status || status !== 200

    if (requestError) {
        return {
            success: false,
            message: "Failed to authenticate",
        }
    }

    if ("challenge" in data) {
        return {
            authenticationChallenge: data.challenge,
            success: false,
            message: "Must complete authentication challenge",
        }
    }

    const id = data.user.id
    const email = data.user.email
    const firstName = data.user.firstName
    const lastName = data.user.lastName

    if (!(isValidString(firstName) && isValidString(lastName))) {
        return {
            success: false,
            message: "Invalid user details",
        }
    }

    try {
        datadogRum.setUser({
            id: id,
            email: email,
            name: `${firstName} ${lastName}`,
        })
    } catch {
        console.warn("Could not set Datadog user details")
    }

    return {
        success: true,
        message: undefined,
        id,
        email,
        firstName,
        lastName,
    }
}

export const login = createAsyncThunk<
    AuthenticationThunkResponse,
    { email: string; password: string }
>("authentication/login", async ({ email, password }, thunkApi) => {
    try {
        const data = await fetchApiGateway("post /auth/login", {
            headers: {
                "nova-auth-strategy": NovaAuthStrategy.COOKIES,
            },
            body: {
                email,
                password,
            },
            region: SupportedRegions.Canada,
        })

        const processedResult = processAuthenticationResponseResults({ data, status: 200 })

        if (!processedResult.success) {
            return thunkApi.rejectWithValue(processedResult)
        }

        thunkApi.dispatch(fetchMe())

        return processedResult
    } catch (error) {
        const apiError = extractApiGatewayError(error)
        if (apiError) {
            console.error(apiError)
            return thunkApi.rejectWithValue(apiError.details)
        }

        return thunkApi.rejectWithValue(error)
    }
})

export const logout = createAsyncThunk(userLogoutAction.type, async () => {
    try {
        await fetchApiGateway("post /auth/logout", { region: SupportedRegions.Canada })
    } catch (error) {
        console.log("Logout post failure:", error)

        // Force logout and clear credentials even if the API call fails
        forceClearAllAppCookies()
        location.href = `/login`
    }

    try {
        datadogRum.addAction(AuthAction.Logout)
    } catch {
        // This is just for user tracking metrics, errors don't need to be handled
    }

    const environmentName = config.get("environmentName")
    const baseUrl = `sso.${getDomainPrefix(environmentName)}mapnova.com`

    const params = new URLSearchParams({
        client_id: config.get("awsUserPoolWebClientId"),
        logout_uri: `${window.location.origin}/login`,
    })

    location.href = `https://${baseUrl}/logout?${params.toString()}`
})

export const getPasswordPolicy = createAsyncThunk<
    PasswordPolicyResponse,
    undefined,
    GenericRejectionResponseWithReason
>("authentication/getPasswordPolicy", async (_, thunkApi) => {
    try {
        const data = await fetchApiGateway("get /auth/policy", { region: SupportedRegions.Canada })

        return {
            minimumLength: data.minimumLength as number,
            temporaryPasswordValidityDays: data.temporaryPasswordValidityDays as number,
            requires: data.requires as PasswordPolicyResponse["requires"],
        }
    } catch {
        return thunkApi.rejectWithValue(false, {
            rejectionReason: "failed to fetch password policy",
        })
    }
})

export const sendPasswordResetCode = createAsyncThunk<PasswordResetResponse, string>(
    "authentication/sendPasswordResetCode",
    async (email, thunkApi) => {
        const bid = await getBrowserFingerprint()
        const data = await fetchApiGateway("post /auth/reset-password", {
            body: {
                email,
                bid,
            },
            region: SupportedRegions.Canada,
        })
        if (!data || (!data.sentResetCode && !data.sentNewPassword)) {
            console.error(data?.message)
            return thunkApi.rejectWithValue(data?.message || "failed to send password reset code")
        }

        return data
    }
)

export const resetPassword = createAsyncThunk<
    PasswordResetResponse,
    {
        email: string
        newPassword: string
        oldPassword?: string
        confirmationCode?: string
    }
>("authentication/resetPassword", async (args, thunkApi) => {
    const { email, newPassword, confirmationCode } = args

    const data = await fetchApiGateway("post /auth/reset-password", {
        body: {
            email,
            newPassword,
            confirmationCode,
        },
        region: SupportedRegions.Canada,
    })

    if (!data || !data.updatedPassword) {
        if (data?.message) {
            console.error(data?.message)
        }
        return thunkApi.rejectWithValue(data?.message || "failed to reset password")
    }

    return data
})

export const refreshCredentials = createAsyncThunk<
    AuthenticationThunkResponse,
    { linkedEmail?: string }
>(
    "authentication/refreshCredentials",
    async (payload, thunkApi) => {
        const { linkedEmail } = payload

        let data
        try {
            data = await fetchApiGateway("get /auth/refresh-credentials", {
                region: SupportedRegions.Canada,
            })
        } catch (error) {
            console.error("refreshCredentials error:", error)
        }

        if (!data) {
            await thunkApi.dispatch(logout())
            return thunkApi.rejectWithValue({
                success: false,
                message: "refresh credentials failed",
            })
        }

        const processedResult = processAuthenticationResponseResults({
            data: data,
            status: 200,
        })

        if (!processedResult.success || (linkedEmail && data?.user?.email !== linkedEmail)) {
            await thunkApi.dispatch(logout())

            return thunkApi.rejectWithValue(processedResult)
        }

        return processedResult
    },
    {
        condition: (_, { getState }) => {
            const state = getState() as RootState

            return selectNeedsCredentialsRefresh(state)
        },
    }
)
